// ---------------------------------------------------------------------------
// Prezio Onboard Widget — Library Entry Point
// ---------------------------------------------------------------------------
// Two consumption modes:
//   1. Vanilla JS: `PrezioOnboard.init({ el, sessionToken, ... })`
//   2. React:      `<PrezioOnboard ... />`
// ---------------------------------------------------------------------------

import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { ApiClient } from '@/api/client';
import { DEFAULT_API_BASE_URL, DEFAULT_THEME } from '@/utils/constants';
import { sanitizeWidgetConfig } from '@/utils/configSanitizer';
import { loadLocale, resolveBrowserLocale } from '@/i18n';
import { PrezioOnboard } from '@/PrezioOnboard';
import type { PrezioOnboardConfig, DestroyHandle } from '@/types/config';

// ---------------------------------------------------------------------------
// init — vanilla JS entry point
// ---------------------------------------------------------------------------

function resolveElement(el: string | HTMLElement): HTMLElement {
  if (typeof el === 'string') {
    const found = document.querySelector<HTMLElement>(el);
    if (!found) throw new Error(`[PrezioOnboard] Element not found: ${el}`);
    return found;
  }
  return el;
}

/**
 * Mount the Prezio Onboard widget into a DOM element.
 *
 * @returns A handle with a `destroy()` method for cleanup.
 */
async function init(config: PrezioOnboardConfig): Promise<DestroyHandle> {
  if (!config.sessionToken) {
    throw new Error('[PrezioOnboard] sessionToken is required');
  }

  // Sanitise server-persisted config (safe defaults when absent / malformed)
  const remote = sanitizeWidgetConfig(config.remoteConfig);

  const rootElement = resolveElement(config.el);
  const baseUrl = config.apiBaseUrl ?? DEFAULT_API_BASE_URL;
  const sessionId = config.sessionId ?? config.sessionToken;
  const locale = config.locale ?? resolveBrowserLocale();

  // Merge theme: explicit ThemeConfig props win over remote config values
  const remoteTheme = {
    ...(remote.colors['primary'] && { primaryColor: remote.colors['primary'] }),
    ...(remote.colors['text'] && { textColor: remote.colors['text'] }),
    ...(remote.colors['background'] && { backgroundColor: remote.colors['background'] }),
    ...(remote.borderRadius && { borderRadius: remote.borderRadius }),
    ...(remote.colorScheme && { colorScheme: remote.colorScheme }),
    ...(remote.logoUrl && { logo: remote.logoUrl }),
  };
  const theme = { ...DEFAULT_THEME, ...remoteTheme, ...config.theme };

  // Explicit props always win over remote config
  const demoMode = config.demoMode ?? remote.demoMode;
  const countryPresets = config.countryPresets ?? (Object.keys(remote.countryPresets).length > 0 ? remote.countryPresets : undefined);

  // Load translations before first render — `en` resolves instantly,
  // other locales are fetched via dynamic import.
  await loadLocale(locale);

  const apiClient = new ApiClient({
    baseUrl,
    sessionId,
    token: config.sessionToken,
  });

  const root: Root = createRoot(rootElement);

  root.render(
    createElement(PrezioOnboard, {
      apiClient,
      sessionId,
      rootElement,
      theme,
      showStepSubtitles: config.showStepSubtitles ?? true,
      demoMode,
      countryPresets,
      upgradeUrl: config.upgradeUrl,
      onComplete: config.onComplete,
      onCancel: config.onCancel,
      onError: config.onError,
      onStepChange: config.onStepChange,
    }),
  );

  return {
    destroy() {
      root.unmount();
    },
  };
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { PrezioOnboard, init };
export { loadLocale, registerLocale, setLocale, getLocale, resolveBrowserLocale } from '@/i18n';
export type { PrezioOnboardConfig, DestroyHandle } from '@/types/config';
export type { ThemeConfig, Locale, ColorScheme } from '@/types/config';
export type { CompletionResult, StepChangeEvent, ErrorEvent } from '@/types/events';
export type { WidgetRemoteConfig, CountryPreset, SanitisedWidgetConfig } from '@/utils/configSanitizer';
export { sanitizeWidgetConfig } from '@/utils/configSanitizer';

// Default export for `PrezioOnboard.init()` UMD usage
export default { init };
